import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, ShieldAlert, Sparkles, Trophy } from "lucide-react";
import {
    ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Cell,
    PieChart, Pie, Legend,
} from "recharts";
import SupplierAvatar from "@/components/SupplierAvatar";

const PROFILE_LABELS = {
    audience: { b2c: "Particuliers", b2b: "Professionnels" },
    priority: { price: "Prix", green: "Vert", service: "Service", digital: "Digital" },
    energy_type: { electricity: "Électricité", gas: "Gaz", both: "Élec + Gaz" },
    green_preference: { low: "Faible", medium: "Moyenne", high: "Forte" },
    budget: { low: "Économique", standard: "Standard", premium: "Premium" },
};

const PIE_COLORS = ["#0A3B2A", "#C4F042", "#3B7A57", "#85A878", "#1E5F44"];

function DistributionChart({ title, data, field }) {
    const formatted = data.map((d, idx) => ({
        name: PROFILE_LABELS[field]?.[d.value] || String(d.value),
        value: d.count,
        fill: PIE_COLORS[idx % PIE_COLORS.length],
    }));
    return (
        <Card className="rounded-3xl border-border">
            <CardContent className="p-6">
                <p className="em-eyebrow">{title}</p>
                <h3 className="font-display text-lg font-bold text-primary mt-1 mb-4">Répartition</h3>
                {formatted.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">Pas encore de données</p>
                ) : (
                    <div className="h-[220px]" data-testid={`chart-${field}`}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={formatted}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={45}
                                    outerRadius={80}
                                    paddingAngle={2}
                                >
                                    {formatted.map((entry) => (
                                        <Cell key={`cell-${entry.name}`} fill={entry.fill} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(152, 9%, 85%)" }} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function AdminAnalyticsAI() {
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/admin/analytics/quiz", { params: { period } });
            setData(d);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, period]);

    if (authLoading) return <div className="em-container py-32 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" /></div>;
    if (!user || user.role !== "admin") {
        return (
            <div className="em-container py-20" data-testid="admin-forbidden">
                <ShieldAlert className="h-12 w-12 text-destructive" />
                <h1 className="font-display text-4xl font-black text-primary mt-4">Accès refusé</h1>
                <Button asChild className="rounded-full mt-6 bg-primary hover:bg-primary/90"><Link to="/">Retour à l'accueil</Link></Button>
            </div>
        );
    }

    return (
        <div className="em-container py-8" data-testid="admin-analytics-ia-page">
            <p className="em-eyebrow flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" />Analytics moteur</p>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-2">Recommandations & Profils</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl">Tendances issues du quiz "Profil idéal" et de l'analyse moteur.</p>

            <div className="mt-6">
                <Tabs value={period} onValueChange={setPeriod}>
                    <TabsList className="rounded-full" data-testid="period-tabs">
                        <TabsTrigger value="7d" className="rounded-full" data-testid="period-7d">7 jours</TabsTrigger>
                        <TabsTrigger value="30d" className="rounded-full" data-testid="period-30d">30 jours</TabsTrigger>
                        <TabsTrigger value="90d" className="rounded-full" data-testid="period-90d">90 jours</TabsTrigger>
                        <TabsTrigger value="all" className="rounded-full" data-testid="period-all">Tout</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {loading || !data ? (
                <div className="py-20 text-center text-muted-foreground" data-testid="analytics-ia-loading">
                    <Loader2 className="h-8 w-8 mx-auto animate-spin" />
                </div>
            ) : (
                <>
                    {/* HEADLINE */}
                    <Card className="rounded-3xl bg-primary text-primary-foreground border-0 mt-10" data-testid="analytics-headline">
                        <CardContent className="p-7 flex items-center gap-5">
                            <div className="h-14 w-14 rounded-2xl bg-accent flex items-center justify-center">
                                <Sparkles className="h-7 w-7 text-accent-foreground" />
                            </div>
                            <div>
                                <p className="text-xs tracking-[0.2em] uppercase text-accent">Quiz exécutés</p>
                                <p className="font-display text-4xl font-black mt-1">{data.total}</p>
                                <p className="text-sm text-primary-foreground/70">recommandations générées au total</p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* TOP SUPPLIERS */}
                    <div className="mt-6">
                        <p className="em-eyebrow mb-3">Fournisseurs les plus recommandés</p>
                        <Card className="rounded-3xl border-border">
                            <CardContent className="p-7">
                                {data.top_suppliers.length === 0 ? (
                                    <p className="text-sm text-muted-foreground py-10 text-center" data-testid="top-suppliers-empty">
                                        Aucun quiz n'a encore été enregistré. Les utilisateurs connectés génèrent des recommandations qui s'agrègent ici.
                                    </p>
                                ) : (
                                    <div className="space-y-4" data-testid="top-suppliers-list">
                                        {data.top_suppliers.map((s, idx) => (
                                            <div key={s.supplier_id} className="flex items-center gap-4" data-testid={`top-supplier-${s.supplier_id}`}>
                                                <div className={`h-8 w-8 rounded-full flex items-center justify-center font-display font-black ${
                                                    idx === 0 ? "bg-accent text-accent-foreground" :
                                                    idx === 1 ? "bg-primary text-primary-foreground" :
                                                    "bg-secondary text-primary"
                                                }`}>
                                                    {idx === 0 ? <Trophy className="h-4 w-4" /> : idx + 1}
                                                </div>
                                                <SupplierAvatar slug={s.supplier_id} name={s.supplier_name} size="sm" />
                                                <div className="flex-1 min-w-0">
                                                    <Link to={`/fournisseurs/${s.supplier_id}`} className="font-display font-bold text-primary hover:underline">
                                                        {s.supplier_name}
                                                    </Link>
                                                    <div className="h-1.5 mt-1 rounded-full bg-secondary overflow-hidden">
                                                        <div
                                                            className="h-full bg-primary rounded-full"
                                                            style={{ width: `${data.top_suppliers[0].count > 0 ? (s.count / data.top_suppliers[0].count) * 100 : 0}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="text-right shrink-0">
                                                    <p className="font-mono font-bold text-primary text-lg">{s.count}</p>
                                                    <p className="text-xs text-muted-foreground">score moy. {s.avg_score}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* DISTRIBUTIONS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="distributions">
                        <DistributionChart title="Audience" data={data.audiences} field="audience" />
                        <DistributionChart title="Priorité principale" data={data.priorities} field="priority" />
                        <DistributionChart title="Énergie demandée" data={data.energy_types} field="energy_type" />
                        <DistributionChart title="Sensibilité écolo" data={data.green_preferences} field="green_preference" />
                        <DistributionChart title="Budget" data={data.budgets} field="budget" />
                    </div>
                </>
            )}
        </div>
    );
}
